// =====================================================
// DesignCustomizer.jsx — Live Frontend Design Anpassung
// Farben · Schriften · Effekte · Layout pro Seite/Sektion
// =====================================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'tef-design-customizer-v1';

function GlassCard({ children, glow = 'cyan', style = {} }) {
  const glowMap = {
    cyan:   { border: 'rgba(56,189,248,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(56,189,248,0.08)' },
    green:  { border: 'rgba(0,245,212,0.18)',   bg: 'rgba(10,25,50,0.55)',  shadow: 'rgba(0,245,212,0.08)' },
    pink:   { border: 'rgba(255,102,162,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(255,102,162,0.08)' },
    purple: { border: 'rgba(167,139,250,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(167,139,250,0.08)' },
    orange: { border: 'rgba(251,146,60,0.18)',  bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(251,146,60,0.08)' },
  };
  const c = glowMap[glow] || glowMap.cyan;
  return (
    <div style={{ borderRadius: 20, border: `1px solid ${c.border}`, background: c.bg, backdropFilter: 'blur(24px) saturate(180%)', WebkitBackdropFilter: 'blur(24px) saturate(180%)', boxShadow: `0 8px 32px ${c.shadow}, inset 0 1px 0 rgba(255,255,255,0.06)`, padding: '18px 16px', ...style }}>
      {children}
    </div>
  );
}

function SectionLabel({ children, icon }) {
  return (
    <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'rgba(200,220,255,0.82)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
      {icon && <span>{icon}</span>}{children}
    </div>
  );
}

function SliderControl({ label, value, onChange, min = 0, max = 100, suffix = '' }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(200,220,255,0.72)' }}>{label}</span>
        <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#38bdf8' }}>{value}{suffix}</span>
      </div>
      <input type="range" min={min} max={max} value={value} onChange={e => onChange(Number(e.target.value))}
        style={{ width: '100%', accentColor: '#38bdf8', height: 4 }} />
    </div>
  );
}

function ColorPicker({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <input type="color" value={value} onChange={e => onChange(e.target.value)}
        style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer', background: 'transparent' }} />
      <div>
        <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(200,220,255,0.72)' }}>{label}</div>
        <div style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.55)', fontFamily: 'monospace' }}>{value}</div>
      </div>
    </div>
  );
}

const PRESETS = [
  { name: '🌊 Ocean Blue', colors: { primary: '#38bdf8', secondary: '#0284c7', accent: '#00f5d4', bg1: '#0a1632', bg2: '#0f2042' } },
  { name: '🟣 Purple Haze', colors: { primary: '#a78bfa', secondary: '#7c3aed', accent: '#f472b6', bg1: '#1a0a32', bg2: '#2d1065' } },
  { name: '🌅 Sunset Orange', colors: { primary: '#fb923c', secondary: '#ea580c', accent: '#fbbf24', bg1: '#1a0f05', bg2: '#2d1a0a' } },
  { name: '🍀 Forest Green', colors: { primary: '#4ade80', secondary: '#16a34a', accent: '#22d3ee', bg1: '#0a1a0f', bg2: '#0f2d14' } },
  { name: '🌙 Midnight Dark', colors: { primary: '#94a3b8', secondary: '#64748b', accent: '#e2e8f0', bg1: '#0a0a0f', bg2: '#111118' } },
  { name: '🔴 o2 Classic', colors: { primary: '#0050aa', secondary: '#003580', accent: '#00a4e0', bg1: '#001122', bg2: '#001e3d' } },
];

const SECTIONS = [
  { id: 'global', label: '🌐 Global', icon: '🌐' },
  { id: 'header', label: '📐 Header', icon: '📐' },
  { id: 'cards', label: '🃏 Tarifkarten', icon: '🃏' },
  { id: 'spielgeld', label: '🎰 Spielgeld', icon: '🎰' },
  { id: 'konfigurator', label: '📋 Konfigurator', icon: '📋' },
];

const DEFAULT_DESIGN = {
  global: {
    primary: '#38bdf8', secondary: '#0284c7', accent: '#00f5d4', bg1: '#0a1632', bg2: '#0f2042',
    borderRadius: 20, glassBlur: 24, glassOpacity: 55, textOpacity: 82,
    fontFamily: 'Poppins', fontSize: 100, shadows: true, animations: true,
  },
  header: { height: 'auto', blur: 24, bgOpacity: 60, showWaves: true },
  cards: { columns: 'auto', gap: 12, borderRadius: 20, glowIntensity: 50 },
  spielgeld: { accentColor: '#00f5d4', bgTint: 'rgba(0,0,0,0.2)' },
  konfigurator: { accentColor: '#38bdf8', bgTint: 'rgba(0,0,0,0.1)' },
};

export function DesignCustomizer() {
  const [design, setDesign] = useState(DEFAULT_DESIGN);
  const [activeSection, setActiveSection] = useState('global');
  const [livePreview, setLivePreview] = useState(true);
  const [savedPresets, setSavedPresets] = useState([]);
  const [presetName, setPresetName] = useState('');

  // Load
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (saved) setDesign(prev => ({ ...prev, ...saved }));
    } catch {}
    try {
      const presets = JSON.parse(localStorage.getItem(STORAGE_KEY + '-presets') || '[]');
      setSavedPresets(presets);
    } catch {}
  }, []);

  // Apply live preview
  useEffect(() => {
    if (!livePreview) return;
    const root = document.documentElement;
    const g = design.global;
    root.style.setProperty('--tef-primary', g.primary);
    root.style.setProperty('--tef-secondary', g.secondary);
    root.style.setProperty('--tef-accent', g.accent);
    root.style.setProperty('--tef-border-radius', g.borderRadius + 'px');
    root.style.setProperty('--tef-glass-blur', g.glassBlur + 'px');
    root.style.setProperty('--tef-glass-opacity', (g.glassOpacity / 100).toString());
    root.style.setProperty('--tef-text-opacity', (g.textOpacity / 100).toString());
    root.style.setProperty('--tef-font-size-scale', (g.fontSize / 100).toString());
    if (g.bg1 && g.bg2) {
      document.body.style.background = `linear-gradient(135deg, ${g.bg1}, ${g.bg2})`;
    }
    return () => {
      // Cleanup: remove custom properties
    };
  }, [design, livePreview]);

  const updateGlobal = useCallback((key, value) => {
    setDesign(prev => ({ ...prev, global: { ...prev.global, [key]: value } }));
  }, []);

  const handleSave = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(design)); } catch {}
  }, [design]);

  const handleReset = useCallback(() => {
    setDesign(DEFAULT_DESIGN);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  }, []);

  const handleSavePreset = useCallback(() => {
    if (!presetName.trim()) return;
    const preset = { name: presetName, design: design.global, timestamp: new Date().toISOString() };
    const updated = [preset, ...savedPresets].slice(0, 20);
    setSavedPresets(updated);
    try { localStorage.setItem(STORAGE_KEY + '-presets', JSON.stringify(updated)); } catch {}
    setPresetName('');
  }, [presetName, design, savedPresets]);

  const applyPreset = useCallback((colors) => {
    setDesign(prev => ({
      ...prev,
      global: { ...prev.global, ...colors },
    }));
  }, []);

  const g = design.global;

  return (
    <div style={{ minHeight: '100vh', color: '#fff', fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '12px 16px 100px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 900, background: 'linear-gradient(135deg, #a78bfa, #ff66a2, #38bdf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>🎨 Design Customizer</div>
          <div style={{ fontSize: '0.68rem', color: 'rgba(200,220,255,0.72)', marginTop: 4 }}>Passe das Frontend-Design live an</div>
        </div>

        {/* Live Preview Toggle */}
        <GlassCard glow="cyan" style={{ marginBottom: 12, padding: '10px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff' }}>Live-Vorschau</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setLivePreview(!livePreview)}
                style={{ padding: '5px 14px', borderRadius: 8, border: `1px solid ${livePreview ? 'rgba(0,245,212,0.4)' : 'rgba(255,255,255,0.1)'}`, background: livePreview ? 'rgba(0,245,212,0.1)' : 'rgba(255,255,255,0.03)', color: livePreview ? '#00f5d4' : 'rgba(200,220,255,0.5)', fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                {livePreview ? '✓ Aktiv' : '○ Inaktiv'}
              </button>
              <button onClick={handleSave}
                style={{ padding: '5px 14px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #38bdf8, #0284c7)', color: '#fff', fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                💾 Speichern
              </button>
              <button onClick={handleReset}
                style={{ padding: '5px 14px', borderRadius: 8, border: '1px solid rgba(255,0,110,0.2)', background: 'rgba(255,0,110,0.06)', color: '#ff006e', fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                ↺ Reset
              </button>
            </div>
          </div>
        </GlassCard>

        {/* Section Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: 4, border: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto' }}>
          {SECTIONS.map(sec => (
            <button key={sec.id} onClick={() => setActiveSection(sec.id)}
              style={{ flex: 1, padding: '10px 6px', borderRadius: 10, border: 'none', background: activeSection === sec.id ? 'rgba(167,139,250,0.15)' : 'transparent', color: activeSection === sec.id ? '#a78bfa' : 'rgba(200,220,255,0.65)', fontSize: '0.58rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.2s' }}>
              {sec.label}
            </button>
          ))}
        </div>

        {/* ========== GLOBAL SECTION ========== */}
        {activeSection === 'global' && (<>
          {/* Presets */}
          <GlassCard glow="purple" style={{ marginBottom: 14 }}>
            <SectionLabel icon="✨">Fertige Presets</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
              {PRESETS.map(preset => (
                <button key={preset.name} onClick={() => applyPreset(preset.colors)}
                  style={{ padding: '10px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)', background: `linear-gradient(135deg, ${preset.colors.bg1}, ${preset.colors.bg2})`, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#fff', marginBottom: 4 }}>{preset.name}</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {Object.entries(preset.colors).filter(([k]) => !k.startsWith('bg')).map(([k, v]) => (
                      <div key={k} style={{ width: 16, height: 16, borderRadius: 4, background: v, border: '1px solid rgba(255,255,255,0.2)' }} />
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </GlassCard>

          {/* Colors */}
          <GlassCard glow="cyan" style={{ marginBottom: 14 }}>
            <SectionLabel icon="🎨">Farben</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <ColorPicker label="Primärfarbe" value={g.primary} onChange={v => updateGlobal('primary', v)} />
              <ColorPicker label="Sekundärfarbe" value={g.secondary} onChange={v => updateGlobal('secondary', v)} />
              <ColorPicker label="Akzentfarbe" value={g.accent} onChange={v => updateGlobal('accent', v)} />
              <ColorPicker label="Hintergrund 1" value={g.bg1} onChange={v => updateGlobal('bg1', v)} />
              <ColorPicker label="Hintergrund 2" value={g.bg2} onChange={v => updateGlobal('bg2', v)} />
            </div>
          </GlassCard>

          {/* Styling */}
          <GlassCard glow="green" style={{ marginBottom: 14 }}>
            <SectionLabel icon="⚙️">Styling</SectionLabel>
            <SliderControl label="Border Radius" value={g.borderRadius} onChange={v => updateGlobal('borderRadius', v)} min={0} max={40} suffix="px" />
            <SliderControl label="Glass Blur" value={g.glassBlur} onChange={v => updateGlobal('glassBlur', v)} min={0} max={60} suffix="px" />
            <SliderControl label="Glass Opacity" value={g.glassOpacity} onChange={v => updateGlobal('glassOpacity', v)} suffix="%" />
            <SliderControl label="Text Helligkeit" value={g.textOpacity} onChange={v => updateGlobal('textOpacity', v)} min={40} max={100} suffix="%" />
            <SliderControl label="Schriftgröße" value={g.fontSize} onChange={v => updateGlobal('fontSize', v)} min={70} max={130} suffix="%" />
          </GlassCard>

          {/* Font */}
          <GlassCard glow="orange" style={{ marginBottom: 14 }}>
            <SectionLabel icon="🔤">Schriftart</SectionLabel>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {['Poppins', 'Inter', 'Roboto', 'Montserrat', 'system-ui'].map(f => (
                <button key={f} onClick={() => updateGlobal('fontFamily', f)}
                  style={{ padding: '8px 14px', borderRadius: 10, border: `1px solid ${g.fontFamily === f ? 'rgba(251,146,60,0.4)' : 'rgba(255,255,255,0.06)'}`, background: g.fontFamily === f ? 'rgba(251,146,60,0.1)' : 'rgba(255,255,255,0.02)', color: g.fontFamily === f ? '#fb923c' : 'rgba(200,220,255,0.72)', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', fontFamily: f }}>
                  {f}
                </button>
              ))}
            </div>
          </GlassCard>

          {/* Toggles */}
          <GlassCard glow="pink" style={{ marginBottom: 14 }}>
            <SectionLabel icon="🎭">Effekte</SectionLabel>
            <div style={{ display: 'flex', gap: 8 }}>
              {[{key:'shadows',label:'Schatten'},{key:'animations',label:'Animationen'}].map(t => (
                <button key={t.key} onClick={() => updateGlobal(t.key, !g[t.key])}
                  style={{ flex: 1, padding: '10px', borderRadius: 10, border: `1px solid ${g[t.key] ? 'rgba(0,245,212,0.3)' : 'rgba(255,255,255,0.06)'}`, background: g[t.key] ? 'rgba(0,245,212,0.08)' : 'rgba(255,255,255,0.02)', color: g[t.key] ? '#00f5d4' : 'rgba(200,220,255,0.62)', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {g[t.key] ? '✓' : '○'} {t.label}
                </button>
              ))}
            </div>
          </GlassCard>

          {/* Save Custom Preset */}
          <GlassCard glow="purple" style={{ marginBottom: 14 }}>
            <SectionLabel icon="💾">Eigenes Preset speichern</SectionLabel>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={presetName} onChange={e => setPresetName(e.target.value)} placeholder="Preset-Name..."
                style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: '0.75rem', fontFamily: 'inherit', outline: 'none' }} />
              <button onClick={handleSavePreset}
                style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #a78bfa, #7c3aed)', color: '#fff', fontSize: '0.72rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
                💾
              </button>
            </div>
            {savedPresets.length > 0 && (
              <div style={{ marginTop: 10 }}>
                {savedPresets.map((p, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.02)', marginTop: 4 }}>
                    <span style={{ fontSize: '0.65rem', color: 'rgba(200,220,255,0.82)' }}>{p.name}</span>
                    <button onClick={() => applyPreset(p.design)}
                      style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(167,139,250,0.3)', background: 'rgba(167,139,250,0.08)', color: '#a78bfa', fontSize: '0.52rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Anwenden
                    </button>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        </>)}

        {/* ========== HEADER SECTION ========== */}
        {activeSection === 'header' && (
          <GlassCard glow="cyan">
            <SectionLabel icon="📐">Header-Einstellungen</SectionLabel>
            <SliderControl label="Blur" value={design.header.blur} onChange={v => setDesign(prev => ({ ...prev, header: { ...prev.header, blur: v } }))} min={0} max={60} suffix="px" />
            <SliderControl label="Hintergrund-Opacity" value={design.header.bgOpacity} onChange={v => setDesign(prev => ({ ...prev, header: { ...prev.header, bgOpacity: v } }))} suffix="%" />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button onClick={() => setDesign(prev => ({ ...prev, header: { ...prev.header, showWaves: !prev.header.showWaves } }))}
                style={{ flex: 1, padding: '10px', borderRadius: 10, border: `1px solid ${design.header.showWaves ? 'rgba(0,245,212,0.3)' : 'rgba(255,255,255,0.06)'}`, background: design.header.showWaves ? 'rgba(0,245,212,0.08)' : 'rgba(255,255,255,0.02)', color: design.header.showWaves ? '#00f5d4' : 'rgba(200,220,255,0.62)', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                {design.header.showWaves ? '✓ Wellen anzeigen' : '○ Wellen ausgeblendet'}
              </button>
            </div>
          </GlassCard>
        )}

        {/* ========== CARDS SECTION ========== */}
        {activeSection === 'cards' && (
          <GlassCard glow="green">
            <SectionLabel icon="🃏">Tarifkarten-Einstellungen</SectionLabel>
            <SliderControl label="Abstand" value={design.cards.gap} onChange={v => setDesign(prev => ({ ...prev, cards: { ...prev.cards, gap: v } }))} min={4} max={32} suffix="px" />
            <SliderControl label="Border Radius" value={design.cards.borderRadius} onChange={v => setDesign(prev => ({ ...prev, cards: { ...prev.cards, borderRadius: v } }))} min={0} max={40} suffix="px" />
            <SliderControl label="Glow-Intensität" value={design.cards.glowIntensity} onChange={v => setDesign(prev => ({ ...prev, cards: { ...prev.cards, glowIntensity: v } }))} suffix="%" />
          </GlassCard>
        )}

        {/* ========== SPIELGELD SECTION ========== */}
        {activeSection === 'spielgeld' && (
          <GlassCard glow="purple">
            <SectionLabel icon="🎰">Spielgeld-Einstellungen</SectionLabel>
            <ColorPicker label="Akzentfarbe" value={design.spielgeld.accentColor} onChange={v => setDesign(prev => ({ ...prev, spielgeld: { ...prev.spielgeld, accentColor: v } }))} />
          </GlassCard>
        )}

        {/* ========== KONFIGURATOR SECTION ========== */}
        {activeSection === 'konfigurator' && (
          <GlassCard glow="orange">
            <SectionLabel icon="📋">Konfigurator-Einstellungen</SectionLabel>
            <ColorPicker label="Akzentfarbe" value={design.konfigurator.accentColor} onChange={v => setDesign(prev => ({ ...prev, konfigurator: { ...prev.konfigurator, accentColor: v } }))} />
          </GlassCard>
        )}

        {/* Preview Card */}
        <GlassCard glow="cyan" style={{ marginTop: 20 }}>
          <SectionLabel icon="👁️">Live-Vorschau</SectionLabel>
          <div style={{ padding: 16, borderRadius: g.borderRadius, background: `linear-gradient(135deg, ${g.bg1}, ${g.bg2})`, border: `1px solid ${g.primary}30`, backdropFilter: `blur(${g.glassBlur}px)` }}>
            <div style={{ fontSize: '1rem', fontWeight: 900, color: g.primary, marginBottom: 6 }}>Beispiel-Karte</div>
            <div style={{ fontSize: '0.72rem', color: `rgba(255,255,255,${g.textOpacity / 100})`, marginBottom: 8 }}>So sieht das Design mit deinen Einstellungen aus.</div>
            <div style={{ display: 'inline-block', padding: '8px 16px', borderRadius: g.borderRadius / 2, background: g.accent, color: '#000', fontSize: '0.68rem', fontWeight: 700 }}>Aktions-Button</div>
          </div>
        </GlassCard>

        {/* Export CSS */}
        <GlassCard glow="green" style={{ marginTop: 14 }}>
          <SectionLabel icon="📋">CSS Variablen (Export)</SectionLabel>
          <pre style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.72)', background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: 12, overflow: 'auto', fontFamily: 'monospace', lineHeight: 1.6, margin: 0 }}>
{`:root {
  --tef-primary: ${g.primary};
  --tef-secondary: ${g.secondary};
  --tef-accent: ${g.accent};
  --tef-bg-1: ${g.bg1};
  --tef-bg-2: ${g.bg2};
  --tef-border-radius: ${g.borderRadius}px;
  --tef-glass-blur: ${g.glassBlur}px;
  --tef-glass-opacity: ${g.glassOpacity / 100};
  --tef-text-opacity: ${g.textOpacity / 100};
  --tef-font-size-scale: ${g.fontSize / 100};
}`}
          </pre>
        </GlassCard>
      </div>
    </div>
  );
}

export default DesignCustomizer;
